import {
  addDiscoverGroupsCarouselGroupApi,
  createDiscoverGroupsCarouselApi,
  deleteDiscoverGroupsCarouselApi,
  deleteDiscoverGroupsCarouselGroupApi,
  fetchSpaceListApiV2,
  getAdminDiscoverGroupsCarouselsApi,
  reorderDiscoverGroupsCarouselGroupsApi,
  reorderDiscoverGroupsCarouselsApi,
  updateDiscoverGroupsCarouselApi,
  updateDiscoverGroupsCarouselGroupApi,
} from '../../util/api';
import { fetchCurrentUser } from '../../ducks/user.duck';

const MAX_ACTIVE_CAROUSELS = 10;
const MAX_ACTIVE_GROUPS_PER_CAROUSEL = 20;

export const FETCH_REQUEST = 'app/AdminPanelDiscoverGroupsCarouselsPage/FETCH_REQUEST';
export const FETCH_SUCCESS = 'app/AdminPanelDiscoverGroupsCarouselsPage/FETCH_SUCCESS';
export const FETCH_ERROR = 'app/AdminPanelDiscoverGroupsCarouselsPage/FETCH_ERROR';
export const FETCH_GROUPS_REQUEST = 'app/AdminPanelDiscoverGroupsCarouselsPage/FETCH_GROUPS_REQUEST';
export const FETCH_GROUPS_SUCCESS = 'app/AdminPanelDiscoverGroupsCarouselsPage/FETCH_GROUPS_SUCCESS';
export const FETCH_GROUPS_ERROR = 'app/AdminPanelDiscoverGroupsCarouselsPage/FETCH_GROUPS_ERROR';
export const MUTATE_REQUEST = 'app/AdminPanelDiscoverGroupsCarouselsPage/MUTATE_REQUEST';
export const MUTATE_SUCCESS = 'app/AdminPanelDiscoverGroupsCarouselsPage/MUTATE_SUCCESS';
export const MUTATE_ERROR = 'app/AdminPanelDiscoverGroupsCarouselsPage/MUTATE_ERROR';

const initialState = {
  carousels: [],
  allGroups: [],
  fetchInProgress: false,
  fetchError: null,
  groupsInProgress: false,
  groupsError: null,
  mutationInProgress: false,
  mutationError: null,
};

const getErrorMessage = error => error?.message || error?.error || 'Something went wrong.';

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case FETCH_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_SUCCESS:
      return { ...state, fetchInProgress: false, carousels: action.payload || [] };
    case FETCH_ERROR:
      return { ...state, fetchInProgress: false, fetchError: action.payload };
    case FETCH_GROUPS_REQUEST:
      return { ...state, groupsInProgress: true, groupsError: null };
    case FETCH_GROUPS_SUCCESS:
      return { ...state, groupsInProgress: false, allGroups: action.payload || [] };
    case FETCH_GROUPS_ERROR:
      return { ...state, groupsInProgress: false, groupsError: action.payload };
    case MUTATE_REQUEST:
      return { ...state, mutationInProgress: true, mutationError: null };
    case MUTATE_SUCCESS:
      return { ...state, mutationInProgress: false };
    case MUTATE_ERROR:
      return { ...state, mutationInProgress: false, mutationError: action.payload };
    default:
      return state;
  }
}

export const fetchDiscoverGroupsCarousels = () => async dispatch => {
  dispatch({ type: FETCH_REQUEST });
  try {
    const response = await getAdminDiscoverGroupsCarouselsApi();
    dispatch({ type: FETCH_SUCCESS, payload: response?.data || [] });
    return response?.data || [];
  } catch (error) {
    dispatch({ type: FETCH_ERROR, payload: getErrorMessage(error) });
    throw error;
  }
};

export const fetchDiscoverGroupsPickerGroups = ({ keyword = '' } = {}) => async dispatch => {
  dispatch({ type: FETCH_GROUPS_REQUEST });
  try {
    const cleanedKeyword = keyword.trim();
    const response = await fetchSpaceListApiV2({
      per_page: 100,
      page: 1,
      sort: 'asc',
      keyword: cleanedKeyword,
    });
    const groups = Array.isArray(response?.records)
      ? response.records
      : Array.isArray(response)
      ? response
      : [];
    dispatch({ type: FETCH_GROUPS_SUCCESS, payload: groups });
    return groups;
  } catch (error) {
    dispatch({ type: FETCH_GROUPS_ERROR, payload: getErrorMessage(error) });
    throw error;
  }
};

const mutateAndRefresh = request => async dispatch => {
  dispatch({ type: MUTATE_REQUEST });
  try {
    const response = await request();
    dispatch({ type: MUTATE_SUCCESS });
    await dispatch(fetchDiscoverGroupsCarousels());
    return response?.data;
  } catch (error) {
    dispatch({ type: MUTATE_ERROR, payload: getErrorMessage(error) });
    throw error;
  }
};

export const createCarousel = payload => async (dispatch, getState) => {
  const carousels = getState().AdminPanelDiscoverGroupsCarouselsPage?.carousels || [];
  if (carousels.length >= MAX_ACTIVE_CAROUSELS) {
    throw new Error(`Only ${MAX_ACTIVE_CAROUSELS} active Discover Groups carousels are allowed.`);
  }
  return dispatch(mutateAndRefresh(() => createDiscoverGroupsCarouselApi(payload)));
};

export const updateCarousel = (carouselId, payload) =>
  mutateAndRefresh(() => updateDiscoverGroupsCarouselApi(carouselId, payload));

export const reorderCarousels = carouselIds =>
  mutateAndRefresh(() => reorderDiscoverGroupsCarouselsApi({ carouselIds }));

export const deleteCarousel = carouselId =>
  mutateAndRefresh(() => deleteDiscoverGroupsCarouselApi(carouselId));

export const addGroupToCarousel = (carouselId, payload) => async (dispatch, getState) => {
  const carousels = getState().AdminPanelDiscoverGroupsCarouselsPage?.carousels || [];
  const carousel = carousels.find(item => String(item.id) === String(carouselId));
  const groups = carousel?.groups || [];
  const isAlreadyAdded = groups.some(group => Number(group.groupId) === Number(payload?.groupId));
  if (!isAlreadyAdded && groups.length >= MAX_ACTIVE_GROUPS_PER_CAROUSEL) {
    throw new Error(`Only ${MAX_ACTIVE_GROUPS_PER_CAROUSEL} active groups are allowed in each carousel.`);
  }
  return dispatch(mutateAndRefresh(() => addDiscoverGroupsCarouselGroupApi(carouselId, payload)));
};

export const reorderCarouselGroups = (carouselId, groupIds) =>
  mutateAndRefresh(() => reorderDiscoverGroupsCarouselGroupsApi(carouselId, { groupIds }));

export const updateCarouselGroup = (groupId, payload) =>
  mutateAndRefresh(() => updateDiscoverGroupsCarouselGroupApi(groupId, payload));

export const deleteCarouselGroup = groupId =>
  mutateAndRefresh(() => deleteDiscoverGroupsCarouselGroupApi(groupId));

export const loadData = () => async (dispatch, getState) => {
  await dispatch(fetchCurrentUser({}));
  const currentUser = getState().user.currentUser;
  const isAdmin =
    currentUser?.attributes?.profile?.privateData?.isDiscoverGroupsCarouselAdmin === true;

  if (!isAdmin) {
    return null;
  }

  return Promise.all([
    dispatch(fetchDiscoverGroupsCarousels()),
    dispatch(fetchDiscoverGroupsPickerGroups()),
  ]);
};
